import { useEffect, useMemo, useState } from "react";
import { Select, Tabs } from "antd";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "./tabs-responsive.css";

function getSelectLabel(item) {
  if (item.selectLabel != null) return item.selectLabel;
  const { label } = item;
  if (typeof label === "string" || typeof label === "number") return String(label);
  return String(item.key);
}

/**
 * Вкладки: на мобильной ширине (≤744px) — Select, иначе antd Tabs.
 * Имя MultiPane без подстрок Nav/Switch/Panels — иначе react/jsx-no-undef в ESLint даёт ложные ошибки.
 * Для сложного label в списке задайте item.selectLabel.
 */
const MultiPane = ({
  items = [],
  activeKey: activeKeyProp,
  defaultActiveKey,
  onChange,
  type = "card",
  className,
  selectClassName,
  ...restTabsProps
}) => {
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";

  const firstKey = items[0]?.key;
  const [innerKey, setInnerKey] = useState(() => defaultActiveKey ?? firstKey);

  const effectiveKey =
    activeKeyProp !== undefined ? activeKeyProp : innerKey;

  useEffect(() => {
    if (activeKeyProp !== undefined) return;
    const keys = items.map((i) => i.key);
    if (effectiveKey != null && !keys.includes(effectiveKey)) {
      setInnerKey(defaultActiveKey ?? firstKey);
    }
  }, [items, activeKeyProp, defaultActiveKey, firstKey, effectiveKey]);

  const handleChange = (key) => {
    onChange?.(key);
    if (activeKeyProp === undefined) setInnerKey(key);
  };

  const options = useMemo(
    () =>
      items.map((item) => ({
        value: item.key,
        label: getSelectLabel(item),
        disabled: item.disabled,
      })),
    [items]
  );

  const activeItem = items.find((i) => i.key === effectiveKey) ?? items[0];
  const pane = activeItem?.children;

  if (!items.length) {
    return null;
  }

  if (!isMobile) {
    return (
      <Tabs
        activeKey={effectiveKey}
        onChange={handleChange}
        items={items}
        type={type}
        className={className}
        {...restTabsProps}
      />
    );
  }

  return (
    <div className={`tabs-responsive-mobile ${className ?? ""}`}>
      <Select
        className={`tabs-responsive-mobile__select ${selectClassName ?? ""}`}
        value={effectiveKey}
        options={options}
        onChange={handleChange}
        popupMatchSelectWidth
        size="large"
      />
      <div className="tabs-responsive-mobile__panel">{pane}</div>
    </div>
  );
};

export default MultiPane;
